import { createServerFn } from "@tanstack/react-start";
import { queryOptions } from "@tanstack/react-query";
import { z } from "zod";
import type { SupabaseClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";
import type { Product } from "@/lib/products";
import { dbError } from "./db";

type Supabase = SupabaseClient<Database>;

// Corpo dos handlers extraído em funções simples (mesmo padrão de
// insertPendingOrder em orders.functions.ts) — ver addresses.functions.ts
// para o motivo: createServerFn nunca popula `context` fora de uma
// requisição real, então testar a const exportada diretamente quebraria.

export async function listWishlist(supabase: Supabase): Promise<Product[]> {
  // Usa o client autenticado (respeita RLS) — mesmo padrão de getMyOrders.
  const { data, error } = await supabase
    .from("wishlist_items")
    .select(
      "product_id, products(*, category:categories(id,name,slug,description,sort_order,parent_id))",
    )
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) throw dbError(error, "src/lib/wishlist.functions.ts");

  return (data ?? [])
    .map((row) => row.products)
    .filter((p): p is NonNullable<typeof p> => !!p) as unknown as Product[];
}

export async function insertWishlistItem(
  supabase: Supabase,
  userId: string,
  productId: string,
): Promise<void> {
  const { error } = await supabase
    .from("wishlist_items")
    .upsert({ user_id: userId, product_id: productId }, { onConflict: "user_id,product_id" });
  if (error) throw dbError(error, "src/lib/wishlist.functions.ts");
}

// Não filtra por user_id — ownership fica a cargo da RLS.
export async function removeWishlistItem(supabase: Supabase, productId: string): Promise<void> {
  const { error } = await supabase.from("wishlist_items").delete().eq("product_id", productId);
  if (error) throw dbError(error, "src/lib/wishlist.functions.ts");
}

export const getMyWishlist = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => listWishlist(context.supabase));

export const myWishlistQuery = () =>
  queryOptions({ queryKey: ["my-wishlist"], queryFn: () => getMyWishlist() });

export const addToWishlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((productId: string) => z.string().uuid().parse(productId))
  .handler(async ({ data: productId, context }) =>
    insertWishlistItem(context.supabase, context.userId, productId),
  );

export const removeFromWishlist = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .validator((productId: string) => z.string().uuid().parse(productId))
  .handler(async ({ data: productId, context }) => removeWishlistItem(context.supabase, productId));
